import { Alert, Button } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useNotification } from '../../../hooks/NotificationContext';
import { addTestCases } from '../../../modules/jira';
import ConfirmationModal from '../../elements/ConfirmationModal';
import Modal from '../../elements/Modal';
import CreateTestCaseForm from './CreateTestCaseForm';

const defaultFormData = {
  priority: '',
  status: '',
  module: null,
  owner: '',
  labels: [],
  testsuites: [],
  estimatedTime: '',
  isAutomated: 'No',
  reasonForNotAutomated: '',
  technologyLayer: '',
  objective: '',
  summary: '',
  preCondition: '',
  reviewer: '',
  automatedBy: '',
  automationMethod: null,
  isAutomationNeeded: false,
  automationStatus: null,
  country: [],
  notAutomatedCategory: null,
  notAutomatedSubCategory: null,
};

const AddTestCaseModal = ({
  showModal,
  handleClose,
  statuses,
  priorities,
  modules,
  labels,
  testSuites,
  projectUsers,
  loading,
  projectId,
  isReviewWorkflow,
  refetchData,
}) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(defaultFormData);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { addNotification } = useNotification();
  const currentUserId = JSON.parse(localStorage.getItem('user')).id;

  const handleFormSubmit = async () => {
    if (!formData.summary?.trim()) {
      addNotification('danger', 'Please add description');
      return;
    }
    setSubmitting(true);
    try {
      const testcase = {
        ...formData,
        reasonForNotAutomated:
          formData.isAutomated === 'No' ? formData.reasonForNotAutomated : '',
      };
      const response = await addTestCases({ testcase }, projectId);

      addNotification(
        'success',
        'Test case created successfully! Redirecting to test case steps...'
      );
      setFormData(defaultFormData);
      navigate(
        `/project/${projectId}/testcases/${response?.test_case?.key}?type=testscripts`
      );
    } catch (error) {
      const errorMessage =
        error?.message || 'Failed to create test case. Please try again later.';
      addNotification('danger', errorMessage);
    }
    setSubmitting(false);
  };

  const handleCloseModal = () => {
    if (formData.name || formData.summary || formData.precondition) {
      setShowConfirmationModal(true);
    } else {
      handleClose();
      setFormData(defaultFormData);
    }
  };

  const handleDiscard = () => {
    setShowConfirmationModal(false);
    handleClose();
    setFormData(defaultFormData);
  };

  const checkIsAdmin = () => {
    const currentUser = projectUsers.find((item) => item.id === currentUserId);
    if (
      currentUser &&
      (currentUser.role === 'admin' || currentUser.role === 'org-admin' || currentUser.role === 'org-manager')
    ) {
      setIsAdmin(true);
    }
  };

  useEffect(() => {
    checkIsAdmin();
  }, [showModal]);

 const disableCreate =
  formData.summary === '' ||
  formData.technologyLayer === '' ||
  formData.testsuites.length === 0 || 
  (formData.isAutomated === 'Yes' && formData?.automationMethod === null) || 
  (formData.isAutomationNeeded === true && formData.isAutomated === 'No' &&
     (formData?.notAutomatedCategory === null || formData?.notAutomatedCategory === '' || 
      formData?.notAutomatedSubCategory === null || formData?.notAutomatedSubCategory === ''));
  
  return (
    <>
      <Modal
        open={showModal}
        handleClose={handleCloseModal}
        modalTitle="Create Test Case"
        action={() => (
          <>
            <Button
              variant="outlined"
              color="secondary"
              disabled={submitting}
              onClick={handleCloseModal}>
              Close
            </Button>
            <Button
              variant="contained"
              // title={disabled ? 'Please fill in all required fields' : 'Create'}
              onClick={handleFormSubmit}
              disabled={disableCreate || submitting}>
              Create
            </Button>
          </>
        )}>
        <div className="my-2">
          {testSuites?.length > 0 ? null : isAdmin ? (
            <Alert severity="warning">
              No test suites found. Please create a test suite to add test
              cases, as they are required for adding test cases.
            </Alert>
          ) : (
            <Alert severity="warning">
              No test suites found. Please ask your project admin to create test
              suites, as they are required for adding test cases.
            </Alert>
          )}
        </div>
        <CreateTestCaseForm
          formData={formData}
          isEditing={true}
          setFormData={setFormData}
          defaultFormData={defaultFormData}
          projectUsers={projectUsers}
          statuses={statuses?.filter((status) => status.name !== 'Archived')}
          priorities={priorities}
          testSuites={testSuites}
          labels={labels}
          modules={modules}
          loading={loading}
          isReviewWorkflow={isReviewWorkflow}
          refetchData={refetchData}
        />
      </Modal>
      <ConfirmationModal
        show={showConfirmationModal}
        handleClose={() => setShowConfirmationModal(false)}
        handleConfirm={handleDiscard}
        title="Discard"
        body="Are you sure you want to cancel? Any unsaved changes will be lost."
        buttonLabel="Discard"
        buttonLabelSecondary="Go Back"
        buttonStyle="primary"
      />
    </>
  );
};

export default AddTestCaseModal;
